import React, { memo, useMemo, useCallback, useEffect, useRef, useState } from 'react';
import { pdfjs, Document, Page } from 'react-pdf';
import { Button, Icon, Spinner, Select } from '@iq/react-components';
import { List } from 'react-window';
import { useClientSize, useEventListener, useThrottledCallback } from '../../../../utils';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';

const MAX_PREVIEW_WIDTH = 136;
const MAX_PREVIEW_HEIGHT = 1.41421 * MAX_PREVIEW_WIDTH;

const PAGE_FIT = 'page-fit';
const PAGE_WIDTH = 'page-width';

const PAGE_MARGIN = 5;

const ZOOM_MAX = 5;
const ZOOM_MIN = 0.25;

pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.mjs';

const availablePercentages = [
  {
    label: 'Actual Size',
    value: 1,
  },
  {
    label: 'Page Fit',
    value: PAGE_FIT,
  },
  {
    label: 'Page Width',
    value: PAGE_WIDTH,
  },
  {
    label: '25%',
    value: 0.25,
  },
  {
    label: '50%',
    value: 0.5,
  },
  {
    label: '75%',
    value: 0.75,
  },
  {
    label: '100%',
    value: 1,
  },
  {
    label: '125%',
    value: 1.25,
  },
  {
    label: '150%',
    value: 1.5,
  },
  {
    label: '200%',
    value: 2,
  },
  {
    label: '300%',
    value: 3,
  },
  {
    label: '400%',
    value: 4,
  },
  {
    label: '500%',
    value: 5,
  },
];

function containZoom(value) {
  return Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, value));
}

const PdfTools = ({ zoomLevel, onSetZoomLevel, options: availableOptions }) => {
  const handleSet = useCallback(
    (item) => {
      onSetZoomLevel(item.value);
    },
    [onSetZoomLevel]
  );

  const options = useMemo(() => {
    const values = new Set();
    return availableOptions.filter((o) => {
      if (values.has(o.value)) {
        return false;
      }
      values.add(o.value);
      return true;
    });
  }, [availableOptions]);

  const value = options.find((item) => item.value === zoomLevel) || {
    value: zoomLevel,
    label: `${Number.parseInt(zoomLevel * 100, 10)}%`,
  };

  return (
    <div className="pdf-tools-wrapper">
      <Select
        className="pdf-size"
        value={value}
        onChange={handleSet}
        options={options}
      />
    </div>
  );
};

const PreviewPageRenderer = memo(
  ({ index, style, scale = 1, pageDimensions, currentPageIndex, scrollToIndex }) => {
    const pageLoaderReference = useRef();

    const onRender = useCallback(() => {
      if (pageLoaderReference.current) {
        pageLoaderReference.current.style.display = 'none';
      }
    }, []);

    const clickHandler = () => {
      if (scrollToIndex) {
        scrollToIndex(index);
      }
    };

    return (
      <div
        style={style}
        className={`preview-page preview-page-${index} ${index === currentPageIndex ? 'active' : ''}`}
      >
        <div
          className="page-loader-component"
          style={{
            width: Math.round(pageDimensions[index].width * scale),
            height: Math.round(pageDimensions[index].height * scale),
          }}
          ref={pageLoaderReference}
        >
          <Spinner />
        </div>
        <Page
          {...{ scale }}
          width={pageDimensions[index].width}
          pageIndex={index}
          onClick={clickHandler}
          onRenderSuccess={onRender}
          renderTextLayer={true}
          renderAnnotationLayer={false}
        />
        <div className="page-number">
          <span>{index + 1}</span>
        </div>
      </div>
    );
  }
);

PreviewPageRenderer.displayName = 'PreviewPageRenderer';

const MainPageRenderer = memo(({ index, style, scale = 1, pageDimensions, containerWidth }) => {
  const pageLoaderReference = useRef();
  const onRender = useCallback(() => {
    if (pageLoaderReference.current) {
      pageLoaderReference.current.style.display = 'none';
    }
  }, []);

  return (
    <div style={{ ...style, width: containerWidth }}>
      <div
        className="page-loader-component"
        style={{
          width: Math.round(pageDimensions[index].width * scale),
          height: Math.round(pageDimensions[index].height * scale),
        }}
        ref={pageLoaderReference}
      >
        <Spinner />
      </div>
      <Page
        {...{ scale }}
        width={pageDimensions[index].width}
        pageIndex={index}
        onRenderSuccess={onRender}
        renderTextLayer={false}
        renderAnnotationLayer={false}
      />
    </div>
  );
});

MainPageRenderer.displayName = 'MainPageRenderer';

const PdfPreview = memo(
  ({
    file,
    setFileInfo,
    activeBookmark,
    setActiveBookmark,
    setBookmarkState,
    fullScreenRef,
    contractedSidebar,
    onStepPrevious,
    onStepNext,
  }) => {
    const [isLoaded, setIsLoaded] = useState(false);
    const [isFullScreen, setIsFullScreen] = useState(false);
    const [pageDimensions, setPageDimensions] = useState();
    const [pageCount, setPageCount] = useState(0);
    const [currentPageIndex, setCurrentPageIndex] = useState(0);
    const [zoomLevel, setZoomLevel] = useState(1);
    const [canToggleNext, setCanToggleNext] = useState(false);
    const [error, setError] = useState(false);

    const isZooming = useRef(false);
    const afterZoomChange = useRef(null);
    const zoomPosition = useRef(null);
    const ctrlZoom = useRef({ zoom: 1, x: 0, y: 0 });
    const mainListReference = useRef(null);
    const mainListOuterReference = useRef(null);
    const mainListInnerReference = useRef();
    const previewListReference = useRef(null);
    const pageOffsets = useRef([]);
    const maxWidth = useRef(0);

    const [clientSize, clientReference] = useClientSize();

    useEffect(() => {
      if (typeof afterZoomChange.current === 'function') {
        afterZoomChange.current();
        afterZoomChange.current = null;
      }
    }, [zoomLevel]);

    const containerDimensions = useMemo(
      () => ({
        ...clientSize,
        // 70 is pageguide height + margin
        height: clientSize.height ? clientSize.height - 70 : 0,
        // 210 is preview width + preview border + margin
        width: clientSize.width ? clientSize.width - 210 : 0,
      }),
      [clientSize]
    );

    const pageFitZoom = useMemo(() => {
      if (!pageDimensions || !containerDimensions || pageDimensions.main.length === 0) {
        return 1;
      }
      const pw = pageDimensions.main.reduce((s, index) => Math.max(s, index.width), 0);
      const ph = pageDimensions.main.reduce((s, index) => Math.max(s, index.height), 0);
      const { width: sw, height: sh } = containerDimensions;
      return containZoom(Math.min(sh / ph, sw / pw));
    }, [pageDimensions, containerDimensions]);

    const pageWidthZoom = useMemo(() => {
      if (!pageDimensions || !containerDimensions || pageDimensions.main.length === 0) {
        return 1;
      }
      const pw = pageDimensions.main.reduce((s, index) => Math.max(s, index.width), 0);
      const { width: sw } = containerDimensions;
      return containZoom(sw / pw);
    }, [pageDimensions, containerDimensions]);

    const availableOptions = useMemo(
      () =>
        availablePercentages.map((item) => {
          if (item.value === PAGE_WIDTH) {
            return {
              ...item,
              value: pageWidthZoom,
            };
          }
          if (item.value === PAGE_FIT) {
            return {
              ...item,
              value: pageFitZoom,
            };
          }
          return item;
        }),
      [pageWidthZoom, pageFitZoom]
    );

    const scrollToZoomPosition = useCallback(
      (position, factor) => {
        const { x, y, sx, sy, marginX } = position;
        const cx = sx + x;
        const cy = sy + y;
        const left = cx * factor - x - marginX * factor;
        const top = cy * factor - y;

        afterZoomChange.current = () => {
          if (mainListOuterReference.current) {
            mainListOuterReference.current.scrollTop = top;
            mainListOuterReference.current.scrollLeft = left;
          }
        };
      },
      [mainListReference, mainListOuterReference]
    );

    const updateZoomLevel = useCallback(
      (newZoomLevel) => {
        setZoomLevel((oldValue) => {
          if (oldValue !== newZoomLevel) {
            if (!mainListOuterReference.current) {
              return newZoomLevel;
            }
            const factor = newZoomLevel / oldValue;
            const { height, width } = mainListOuterReference.current.getBoundingClientRect();
            const sx = mainListOuterReference.current.scrollLeft;
            const sy = mainListOuterReference.current.scrollTop;
            const marginX = Math.max(
              0,
              (containerDimensions.width - maxWidth.current * oldValue) / 2
            );
            scrollToZoomPosition(
              {
                x: width / 2,
                y: height / 2,
                sx,
                sy,
                marginX,
              },
              factor
            );
            return newZoomLevel;
          }
          return oldValue;
        });
      },
      [scrollToZoomPosition, containerDimensions.width, mainListOuterReference]
    );

    const containerRef = React.useCallback(
      (node) => {
        clientReference(node);

        fullScreenRef.current = node;
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [clientReference, fullScreenRef, isFullScreen]
    );

    const simulateZoom = useCallback((x, y, zoom) => {
      if (mainListInnerReference.current) {
        mainListInnerReference.current.style.transform =
          x === undefined
            ? 'translate(0px, 0px) scale(1)'
            : `translate(${Math.round(x)}px, ${Math.round(y)}px) scale(${zoom})`;
      }
    }, []);

    const handleZoom = useCallback(
      (event) => {
        if (isZooming.current) {
          event.preventDefault();
          const maxZoom = ZOOM_MAX / zoomLevel;
          const minZoom = ZOOM_MIN / zoomLevel;

          const { zoom: oldZoom, x: oldX, y: oldY } = ctrlZoom.current;

          let factor = Math.min(1.25, Math.max(0.85, event.deltaY * -0.01 + 1));
          let zoom = oldZoom * factor;

          if (zoom < minZoom) {
            factor = minZoom / oldZoom;
            zoom = minZoom;
          }
          if (zoom > maxZoom) {
            factor = maxZoom / oldZoom;
            zoom = maxZoom;
          }

          const innerRect = mainListInnerReference.current.getBoundingClientRect();
          const dx = Math.round((event.clientX - innerRect.left) * (factor - 1));
          const dy = Math.round((event.clientY - innerRect.top) * (factor - 1));

          const x = oldX - dx;
          const y = oldY - dy;

          ctrlZoom.current = {
            zoom,
            x,
            y,
          };

          simulateZoom(x, y, zoom);

          if (zoomPosition.current === null) {
            const { left, top } = mainListOuterReference.current.getBoundingClientRect();
            const sx = mainListOuterReference.current.scrollLeft;
            const sy = mainListOuterReference.current.scrollTop;
            const marginX = Math.max(
              0,
              (containerDimensions.width - maxWidth.current * zoomLevel) / 2
            );
            zoomPosition.current = {
              x: event.clientX - left,
              y: event.clientY - top,
              sx,
              sy,
              marginX,
            };
          }
        }
      },
      [simulateZoom, zoomLevel, containerDimensions.width, mainListOuterReference]
    );

    const handleKeyDown = useCallback(
      (event) => {
        if (event.ctrlKey && mainListOuterReference.current && !isZooming.current) {
          isZooming.current = true;
          zoomPosition.current = null;
          mainListOuterReference.current.classList.add('zooming');
        }
      },
      [mainListOuterReference]
    );

    const stopZooming = useCallback(() => {
      if (isZooming.current) {
        isZooming.current = false;
        const { zoom } = ctrlZoom.current;
        if (zoom !== 1) {
          const factor = containZoom(zoom * zoomLevel) / zoomLevel;
          scrollToZoomPosition(zoomPosition.current, factor);
          setZoomLevel(zoomLevel * factor);
          mainListOuterReference.current.classList.remove('zooming');
        }
        simulateZoom();
        ctrlZoom.current = {
          zoom: 1,
          x: 0,
          y: 0,
        };
      }
    }, [zoomLevel, simulateZoom, scrollToZoomPosition, setZoomLevel, mainListOuterReference]);

    const maybePreventNativeZoom = useCallback(
      (event) => {
        if (isZooming.current) {
          event.preventDefault();
        }
      },
      [isZooming]
    );

    const handleFullScreenChange = () => setIsFullScreen(() => !!document.fullscreenElement);

    useEventListener('keydown', handleKeyDown);
    useEventListener('wheel', handleZoom, mainListOuterReference.current);
    useEventListener('keyup', stopZooming);
    useEventListener('mouseleave', stopZooming, mainListOuterReference.current);
    useEventListener('mousewheel', maybePreventNativeZoom);
    useEventListener('DOMMouseScroll', maybePreventNativeZoom);
    useEventListener('fullscreenchange', handleFullScreenChange);

    const onKeyDown = useCallback(
      (event) => {
        if (event.keyCode === 39 && onStepNext) {
          event.preventDefault();
          event.stopPropagation();
          onStepNext(file.id);
        }
        if (event.keyCode === 37 && onStepPrevious) {
          event.preventDefault();
          event.stopPropagation();
          onStepPrevious(file.id);
        }
      },
      [onStepNext, onStepPrevious, file.id]
    );

    useEffect(() => {
      if (canToggleNext) {
        document.addEventListener('keydown', onKeyDown);
      }
      return () => {
        document.removeEventListener('keydown', onKeyDown);
      };
    }, [canToggleNext, onKeyDown]);

    useEffect(() => {
      setBookmarkState({
        bookmarkData: {
          page: currentPageIndex + 1,
        },
      });
    }, [currentPageIndex, setBookmarkState]);

    const maybeClearActiveBookmark = useCallback(
      (pageNumber) => {
        if (activeBookmark) {
          const { page: activeBookmarkPage } = activeBookmark.bookmarkData;
          if (pageNumber !== activeBookmarkPage) {
            setActiveBookmark(null);
          }
        }
      },
      [activeBookmark, setActiveBookmark]
    );

    const updateCurrentPage = useCallback(
      (pageIndex) => {
        if (pageIndex === currentPageIndex) {
          return;
        }

        maybeClearActiveBookmark(pageIndex + 1);

        setCurrentPageIndex(pageIndex);

        if (previewListReference.current) {
          // If the item is already visible, it won't scroll at all.
          previewListReference.current.scrollToRow({ index: pageIndex });
        }
      },
      [currentPageIndex, maybeClearActiveBookmark]
    );

    const scrollToIndex = useCallback(
      (index) => {
        if (mainListReference.current) {
          mainListReference.current.scrollToRow({ index, align: 'start' });
          updateCurrentPage(index);
        }
      },
      [updateCurrentPage]
    );

    const onMainListScroll = useThrottledCallback(
      () => {
        if (!mainListOuterReference.current || !pageDimensions) {
          return;
        }

        const scrollOffset = mainListOuterReference.current.scrollTop;

        const atTop = scrollOffset === 0;
        const atBottom =
          (scrollOffset + containerDimensions.height) / zoomLevel >=
          pageOffsets.current[pageCount] - pageDimensions.main[pageCount - 1].height * 0.5;

        if (atTop) {
          updateCurrentPage(0);
        } else if (atBottom) {
          updateCurrentPage(pageCount - 1);
        } else {
          const middle = containerDimensions.height * 0.5;
          const pageNumber = pageOffsets.current.findIndex(
            (po) => (scrollOffset + middle) / zoomLevel < po
          );
          updateCurrentPage(pageNumber - 1);
        }
      },
      500,
      { leading: false, trailing: true },
      [pageOffsets, containerDimensions, pageDimensions, pageCount]
    );

    useEffect(() => {
      if (isLoaded && activeBookmark) {
        const { page: pageString } = activeBookmark.bookmarkData;
        const pageNumber = Number.parseInt(pageString, 10);
        scrollToIndex(pageNumber - 1);
      }
    }, [isLoaded, activeBookmark, scrollToIndex]);

    const onDocumentLoadSuccess = useCallback(
      (pdf) => {
        setPageCount(pdf.numPages);
        setFileInfo({
          type: 'pdf',
          pages: pdf.numPages,
        });
        Promise.all(
          Array.from({ length: pdf.numPages }, (v, index) => index + 1).map(pdf.getPage.bind(pdf))
        ).then((meta) => {
          const dimensions = meta.reduce(
            (acc, page) => {
              const [x1, y1, x2, y2] = page.view;
              const isRotated = page.rotate === 90 || page.rotate === 270;
              const width = isRotated ? y2 - y1 : x2 - x1;
              const height = isRotated ? x2 - x1 : y2 - y1;
              // https://mozilla.github.io/pdf.js/api/draft/module-pdfjsLib-PDFPageProxy.html#pageNumber
              acc.main[page.pageNumber - 1] = {
                width,
                height,
              };
              const pr = Math.min(MAX_PREVIEW_WIDTH / width, MAX_PREVIEW_HEIGHT / height);

              // https://mozilla.github.io/pdf.js/api/draft/module-pdfjsLib-PDFPageProxy.html#pageNumber
              acc.preview[page.pageNumber - 1] = {
                width: width * pr,
                height: height * pr,
              };
              return acc;
            },
            { main: [], preview: [] }
          );
          setPageDimensions(dimensions);
          pageOffsets.current = dimensions.main.reduce(
            (acc, current) => {
              acc.push([...acc].toReversed()[0] + current.height + PAGE_MARGIN);
              return acc;
            },
            [0]
          );
          maxWidth.current = dimensions.main.reduce((s, index) => Math.max(s, index.width), 0);
          const maxHeight = dimensions.main.reduce((s, index) => Math.max(s, index.height), 0);
          const initialZoom = Math.min(
            containerDimensions.width / maxWidth.current,
            containerDimensions.height / maxHeight
          );
          setZoomLevel(containZoom(initialZoom));
          if (mainListReference.current) {
            afterZoomChange.current = () => {
              mainListReference.current.scrollToRow({ index: currentPageIndex });
            };
          }
          setIsLoaded(true);
          setCanToggleNext(true);
        });
      },
      [containerDimensions, currentPageIndex, setFileInfo]
    );

    const onDocumentLoadError = useCallback((error_) => {
      setError(error_);
    }, []);

    const getPreviewItemSize = useCallback(
      (index) => Math.round(pageDimensions.preview[index].height) + 50,
      [pageDimensions]
    );

    const getMainItemSize = useCallback(
      (index) => Math.round((pageDimensions.main[index].height + PAGE_MARGIN) * zoomLevel),
      [pageDimensions, zoomLevel]
    );

    const previewItemData = useMemo(
      () => ({
        currentPageIndex,
        scrollToIndex,
        pageDimensions: pageDimensions && pageDimensions.preview,
      }),
      [scrollToIndex, pageDimensions, currentPageIndex]
    );

    const mainItemData = useMemo(
      () => ({
        scale: zoomLevel,
        pageDimensions: pageDimensions && pageDimensions.main,
        containerWidth: pageDimensions
          ? pageDimensions.main.reduce(
              (s, index) => Math.max(s, index.width * zoomLevel),
              containerDimensions.width
            )
          : 0,
      }),
      [zoomLevel, pageDimensions, containerDimensions]
    );

    return (
      <div
        className={
          contractedSidebar ? 'pdf-preview-component' : 'pdf-preview-component smaller-screens'
        }
        ref={containerRef}
      >
        {!isLoaded && !error && (
          <div className="spinner-wrapper">
            <Spinner />
          </div>
        )}
        {error && <div className="pdf-preview-component__error">Failed to load PDF file.</div>}
        <Document
          file={file}
          onLoadSuccess={onDocumentLoadSuccess}
          onLoadError={onDocumentLoadError}
          error=""
        >
          {isLoaded && containerDimensions && (
            <>
              <div className="pages-preview">
                <div className="page-guide-pages">
                  {currentPageIndex + 1} / {pageCount}
                </div>
                <List
                  defaultHeight={containerDimensions.height}
                  style={{ width: 150, height: containerDimensions.height }}
                  rowCount={pageCount}
                  rowHeight={getPreviewItemSize}
                  rowComponent={PreviewPageRenderer}
                  rowProps={previewItemData}
                  listRef={(node) => {
                    previewListReference.current = node;
                    if (node && node.element) {
                      node.element.classList.add('obtrusive-scrollbar', 'custom-scrollbar');
                    }
                  }}
                />
              </div>
              <div className="main-window">
                <div className="page-guide">
                  <div
                    className="title"
                    title={file.name}
                  >
                    {file.name}
                  </div>
                  <PdfTools
                    zoomLevel={zoomLevel}
                    onSetZoomLevel={updateZoomLevel}
                    options={availableOptions}
                  />
                  <div className="change-file">
                    {onStepPrevious && (
                      <Button
                        activity="secondary"
                        disabled={!canToggleNext}
                        onClick={() => onStepPrevious()}
                        icon={
                          <Icon
                            size="xs"
                            icon="he-backwards"
                          />
                        }
                        tooltip="Previous"
                        tooltipPosition="bottom"
                      />
                    )}
                    {onStepNext && (
                      <Button
                        activity="secondary"
                        disabled={!canToggleNext}
                        onClick={() => onStepNext()}
                        icon={
                          <Icon
                            size="xs"
                            icon="he-forwards"
                          />
                        }
                        tooltip="Next"
                        tooltipPosition="bottom"
                      />
                    )}
                  </div>
                </div>
                <List
                  defaultHeight={containerDimensions.height}
                  style={{ width: containerDimensions.width, height: containerDimensions.height }}
                  rowCount={pageCount}
                  rowHeight={getMainItemSize}
                  rowComponent={MainPageRenderer}
                  rowProps={mainItemData}
                  listRef={(node) => {
                    mainListReference.current = node;
                    if (node && node.element) {
                      mainListOuterReference.current = node.element;
                      mainListInnerReference.current = node.element.firstChild;
                      node.element.classList.add('obtrusive-scrollbar', 'custom-scrollbar');
                    }
                  }}
                  onScroll={onMainListScroll}
                />
              </div>
            </>
          )}
        </Document>
      </div>
    );
  },
  (previousProperties, properties) =>
    previousProperties.file.name === properties.file.name &&
    previousProperties.activeBookmark === properties.activeBookmark
);

export default PdfPreview;
